import {ObjectModelDefaultActionType} from "~/enums/objectModelDefaultActionType";
import assertNever from "../../../global/assetNever";
import {ObjectModelActionDefaultLabels, ObjectModelActionType} from "../../enums/objectModelActionType";
import {EnumHelper} from "../../helpers/enumHelper";
import {ObjectModelPropertyCapability} from "../objectModelProperties/objectModelPropertyCapability";
import ObjectModelAction from "./objectModelAction";
import {ObjectModelActionCapability} from "./objectModelActionCapability";
import {DefaultEmailStyle} from "~/enums/defaultEmailStyle";

export default class ObjectModelActionCapabilityManager {
  static debug: boolean = false;

  static LABELABLE_ACTIONS: ObjectModelActionType[] = [
    ObjectModelActionType.DefaultAction,
    ObjectModelActionType.FillForm,
    ObjectModelActionType.ChangeFieldValues,
    ObjectModelActionType.CustomNotification,
    ObjectModelActionType.ViewDocument,
    ObjectModelActionType.RunProcess,
    ObjectModelActionType.LinkToAccount,
  ];

  static FORM_LINKABLE_ACTIONS: ObjectModelActionType[] = [
    ObjectModelActionType.FillForm,
  ];

  static EMAIL_STYLEABLE_ACTIONS: ObjectModelActionType[]=[
    ObjectModelActionType.CustomNotification,
  ]

  static AVAILABILITY_CONDITIONABLE_ACTIONS: ObjectModelActionType[] = [
    ObjectModelActionType.FillForm,
    ObjectModelActionType.ChangeFieldValues,
    ObjectModelActionType.ViewDocument,
    ObjectModelActionType.RunProcess,
    ObjectModelActionType.LinkToAccount,
  ];

  static BULK_ACTIONABLE = [
    ObjectModelActionType.ChangeFieldValues,
    ObjectModelActionType.CustomNotification,
    ObjectModelActionType.RunProcess,
    ObjectModelActionType.LinkToAccount,
  ];

  static RUN_PROCESSABLE = [
    ObjectModelActionType.RunProcess,
  ]

  static CONFIRMATION_CUSTOMIZABLE = [
    ObjectModelActionType.DefaultAction,
    ObjectModelActionType.ChangeFieldValues,
    ObjectModelActionType.RunProcess,
    ObjectModelActionType.LinkToAccount,
  ];

  static LOGGABLE_ACTIONS: ObjectModelActionType[] = [
    ObjectModelActionType.DefaultAction,
    ObjectModelActionType.FillForm,
    ObjectModelActionType.ChangeFieldValues,
    ObjectModelActionType.CustomNotification,
    ObjectModelActionType.ViewDocument,
    ObjectModelActionType.RunProcess,
    ObjectModelActionType.LinkToAccount,
  ];

  static DOCUMENT_LINKABLE: ObjectModelActionType[] = [
    ObjectModelActionType.ViewDocument,
  ];

  static NOTE_REQUIREABLE: ObjectModelActionType[] = [
    // includes 'Edit' default action, handled via secondaryType in `can()`
    ObjectModelActionType.ChangeFieldValues,
    ObjectModelActionType.LinkToAccount,
  ]

  static ACCOUNT_LINKABLE: ObjectModelActionType[] = [
    ObjectModelActionType.LinkToAccount,
  ]

  static can(type: ObjectModelActionType, secondaryType: ObjectModelDefaultActionType, duty: ObjectModelActionCapability): boolean {
    let can = false;
    switch (duty) {
      case ObjectModelActionCapability.labelable:
        can = ObjectModelActionCapabilityManager.LABELABLE_ACTIONS.includes(type);
        break;
      case ObjectModelActionCapability.formLinkable:
        can = ObjectModelActionCapabilityManager.FORM_LINKABLE_ACTIONS.includes(type);
        break;
      case ObjectModelActionCapability.availabilityConditionable:
        can = ObjectModelActionCapabilityManager.AVAILABILITY_CONDITIONABLE_ACTIONS.includes(type) || secondaryType === ObjectModelDefaultActionType.Delete || secondaryType === ObjectModelDefaultActionType.Edit;
        break;
      case ObjectModelActionCapability.bulkActionable:
        can = ObjectModelActionCapabilityManager.BULK_ACTIONABLE.includes(type) || (type === ObjectModelActionType.DefaultAction && secondaryType === ObjectModelDefaultActionType.Delete)
        break;
      case ObjectModelActionCapability.confirmationCustomizable:
        can = ObjectModelActionCapabilityManager.CONFIRMATION_CUSTOMIZABLE.includes(type);
        break;
      case ObjectModelActionCapability.loggable:
        if (type !== ObjectModelActionType.DefaultAction || secondaryType !== ObjectModelDefaultActionType.Delete) {
          can = ObjectModelActionCapabilityManager.LOGGABLE_ACTIONS.includes(type);
        }
        break;
      case ObjectModelActionCapability.documentLinkable:
        can = ObjectModelActionCapabilityManager.DOCUMENT_LINKABLE.includes(type);
        break;
      case ObjectModelActionCapability.noteRequireable:
        can = ObjectModelActionCapabilityManager.NOTE_REQUIREABLE.includes(type) || secondaryType === ObjectModelDefaultActionType.Edit;
        break;
      case ObjectModelActionCapability.runProcessable:
        can = ObjectModelActionCapabilityManager.RUN_PROCESSABLE.includes(type);
        break;
      case ObjectModelActionCapability.accountLinkable:
        can = ObjectModelActionCapabilityManager.ACCOUNT_LINKABLE.includes(type);
        break;
      case ObjectModelActionCapability.emailStyleable:
        can = ObjectModelActionCapabilityManager.EMAIL_STYLEABLE_ACTIONS.includes(type)
        break
      default:
        assertNever(duty);
        can = false;
        break;
    }
    ObjectModelActionCapabilityManager.log(`TYpe ${ObjectModelActionType[type]} ${can ? 'can' : 'can\'t'} ${ObjectModelPropertyCapability[duty]}`);
    return can;
  }

  static addDefaultProperties(action: ObjectModelAction) {
    EnumHelper.toArray(ObjectModelActionCapability).forEach((c: ObjectModelActionCapability) => {
      switch (c) {
        case ObjectModelActionCapability.labelable:
          const defaultLabel = ObjectModelActionDefaultLabels[action.action_type];
          action.defineProperty('label_title', defaultLabel);
          break;
        case ObjectModelActionCapability.formLinkable:
          action.defineProperty('linked_form_id', null);
          action.defineProperty('restrict_form_viewers', false);
          action.defineProperty('require_relationship', true);
          action.defineProperty('open_in_new_tab', false);
          break;
        case ObjectModelActionCapability.availabilityConditionable:
          action.defineProperty('has_availability_condition', false);
          break;
        case ObjectModelActionCapability.bulkActionable:
          action.defineProperty('allows_bulk', false);
          break;
        case ObjectModelActionCapability.confirmationCustomizable:
          action.defineProperty('include_confirmation', true);
          break;
        case ObjectModelActionCapability.loggable:
          action.defineProperty('is_logged', false);
          break;
        case ObjectModelActionCapability.documentLinkable:
          action.defineProperty('linked_document_id', '');
          break;
        case ObjectModelActionCapability.noteRequireable:
          action.defineProperty('require_note', false);
          break;
        case ObjectModelActionCapability.runProcessable:
          action.defineProperty('linked_process_flow_id', '');
          action.defineProperty('treat_as_bulk', false);
          break;
        case ObjectModelActionCapability.accountLinkable:
          action.defineProperty('linked_account_relationship_id', '');
          action.defineProperty('linked_account_relationship_title', '');
          break
        case ObjectModelActionCapability.emailStyleable:
          action.defineProperty('email_style', DefaultEmailStyle.Default);
          break;
        default:
          assertNever(c);
          break;
      }
    });
  }

  private static log(message: string) {
    if (this.debug) {
      console.log(`DEBUG: ObjectModelActionCan: ${message}`);
    }
  }
}